import { gameKey as strictGameKey, parseGameKey } from 'config-hub-server/src/model/keys.js';
import { setParams } from './urlParams.js';

/**
 * Route paths and the URLs built from them, in one place so a page's `<Route path>` and the
 * links into it cannot disagree. Per-type dispatch lives in `domain/entityTypes.ts`.
 *
 * react-router 6.30's `generatePath` does NOT encode params, so a builder below encodes its own
 * path segments; a query string is built with `URLSearchParams`, never string concatenation.
 */

/**
 * `path`, or `path?query` when there is one. `params` is either a patch built into a query with
 * `setParams` (empty values dropped), or an already-built query string (a filter's own writer,
 * e.g. `rowFilterQuery`/`writeAuditFilter`) taken as is.
 */
export const withQuery = (path: string, params: Record<string, string | null | undefined> | string): string => {
    const query = typeof params === 'string' ? params : setParams(new URLSearchParams(), params).toString();
    return query ? `${path}?${query}` : path;
};

/** `pattern`'s `:name` segments filled from `params`, each `encodeURIComponent`'d — `generatePath` does not encode, and `domain/` may not import react-router. */
const fillPath = (pattern: string, params: Record<string, string>): string =>
    pattern.replace(/:(\w+)/g, (_, name: string) => encodeURIComponent(params[name] ?? ''));

export const STUDIOS_ROUTE = '/studios';
export const NEW_STUDIO_PATH = '/studios/new';
export const STUDIO_EDIT_PATH = '/studios/:id/edit';
export const studioEditRoute = (id: string): string => fillPath(STUDIO_EDIT_PATH, { id });
/** `/studios/:key` — there is no per-studio page; it redirects to the Games page filtered to it. */
export const STUDIO_REDIRECT_PATH = '/studios/:key';

export const GAMES_ROUTE = '/games';
export const NEW_GAME_PATH = '/games/new';
export const GAME_EDIT_PATH = '/games/:studio/:id/edit';
export const gameEditRoute = (studio: string, id: string): string => fillPath(GAME_EDIT_PATH, { studio, id });
/** `/games/:studio/:id/assets` — from before assets became the editor's last section; keeps old links working. */
export const GAME_ASSETS_REDIRECT_PATH = '/games/:studio/:id/assets';

export const CONFIGS_ROUTE = '/configs';
export const NEW_CONFIG_PATH = '/configs/new';
export const CONFIG_EDIT_PATH = '/configs/:name/edit';
export const configEditRoute = (name: string): string => fillPath(CONFIG_EDIT_PATH, { name });

export const ENVIRONMENTS_ROUTE = '/environments';
export const AUDIT_ROUTE = '/audit';
export const USERS_ROUTE = '/users';
export const LOGIN_ROUTE = '/login';

/** The Games page's studio filter (`pages/games/gamesFilter.ts`'s `readStudio`/`writeStudio`) and the create pages' preset. */
export const STUDIO_PARAM = 'studio';
/** The game editor's Assets section, opened on one file kind. */
export const KIND_PARAM = 'kind';
export const ASSETS_HASH = '#assets';

/** The new-game page's source: `?copyFrom=<studio>/<id>` opens it prefilled from that master game. */
export const COPY_FROM_PARAM = 'copyFrom';

/** The create page; a studio's `?studio=` preset for the drawer's "Add game", and the game a copy starts from. */
export const newGameRoute = (studio?: string | null, copyFrom?: string | null): string =>
    withQuery(NEW_GAME_PATH, { [STUDIO_PARAM]: studio, [COPY_FROM_PARAM]: copyFrom });

/** The Games page, filtered to one studio when given. */
export const gamesRoute = (studio?: string | null): string => withQuery(GAMES_ROUTE, { [STUDIO_PARAM]: studio });

/** The game editor scrolled to its Assets section, opened on one kind when given. */
export const gameAssetsRoute = (studio: string, id: string, kind?: string | null): string =>
    `${withQuery(gameEditRoute(studio, id), { [KIND_PARAM]: kind })}${ASSETS_HASH}`;

/**
 * A game key is `<studio>/<id>`, split on the FIRST separator (a studio id never contains one).
 * Lenient, for callers that would rather show a stray key than crash on one: a key the server's
 * total `parseGameKey` cannot split reads as a studio with an empty `id` — the whole key, or the
 * studio before the separator when it is `acme/` (a new game with no id typed yet).
 */
export const splitGameKey = (key: string): { studio: string; id: string } =>
    parseGameKey(key) ?? { studio: key, id: '' };

/** The inverse of `splitGameKey`. */
export const gameKey = ({ studio, id }: { studio: string; id: string }): string => strictGameKey(studio, id);
